import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import { ConfigProvider, Switch, Tabs, Select, Flex, Dropdown } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { Hint } from "@/common/hint";
import LayoutToggleComponent from "@/common/layout-toggle";
import "@/assets/less/dashboard-tabs.less";

const getWindowWidth = () =>
  typeof window === "undefined"
    ? 1920
    : Math.round(window.visualViewport?.width || window.innerWidth);

const TAB_GUTTER = 8;
const NAV_GAP = 16;
const TAB_LAYOUT_BUFFER = 16;
const TAB_WIDTH_BUFFER = 32;

const getOuterWidth = (element) => {
  if (!element) {
    return 0;
  }

  return element.getBoundingClientRect().width;
};

export const DashboardTabs = ({
  dashboardItems,
  children,
  setShowFilterPanel,
  rightTabs,
  setActiveTabKey,
  subTabActiveKey,
  enableLayoutToggle = true,
  compactRightTabs = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  // const { sideView } = useSelector(selectCurrentDashboardFields);
  const current = location.pathname;
  const subTabActive =
    subTabActiveKey !== "1" &&
    (location.pathname === "/" || location.pathname === "/approval-summary");
  const [width, setWidth] = useState(getWindowWidth);
  const [layoutRevision, setLayoutRevision] = useState(0);
  const [visibleTabCount, setVisibleTabCount] = useState(() => dashboardItems.items.length);
  const dashboardTabsRef = useRef(null);
  const tabBarWrapperRef = useRef(null);
  const tabMeasureRef = useRef(null);
  const rightControlsRef = useRef(null);
  const layoutFrameRef = useRef(null);
  const settleTimerRef = useRef(null);
  const lastWindowWidthRef = useRef(getWindowWidth());
  const resolvedVisibleTabCount = Math.min(visibleTabCount, dashboardItems.items.length);
  const visibleDashboardItems = dashboardItems.items.slice(0, resolvedVisibleTabCount);
  const overflowDashboardItems = dashboardItems.items.slice(resolvedVisibleTabCount);
  const activeOverflowTab = overflowDashboardItems.some((item) => item.url === current);
  const tabsLayoutKey = `${width}-${resolvedVisibleTabCount}-${dashboardItems.items.length}`;

  const updateVisibleTabCount = useCallback(() => {
    const currentWidth = getWindowWidth();

    const setNextVisibleTabCount = (nextVisibleTabCount) => {
      setVisibleTabCount((previousVisibleTabCount) =>
        previousVisibleTabCount === nextVisibleTabCount
          ? previousVisibleTabCount
          : nextVisibleTabCount
      );
    };

    if (currentWidth < 768) {
      setNextVisibleTabCount(dashboardItems.items.length);
      return;
    }

    const tabMeasure = tabMeasureRef.current;
    const dashboardTabs = dashboardTabsRef.current;
    if (!tabMeasure || !dashboardTabs) {
      return;
    }

    const measuredTabs = Array.from(
      tabMeasure.querySelectorAll("[data-dashboard-tab-measure]")
    );

    if (!measuredTabs.length) {
      setNextVisibleTabCount(0);
      return;
    }

    const tabWidths = measuredTabs.map(
      (tab) => tab.getBoundingClientRect().width + TAB_WIDTH_BUFFER
    );
    const moreButtonWidth =
      tabMeasure.querySelector(".dashboard-tabs-more-button")?.getBoundingClientRect().width || 0;
    const dashboardTabsRect = dashboardTabs.getBoundingClientRect();
    const viewportWidth = currentWidth;
    const visibleContainerWidth = Math.min(
      dashboardTabsRect.width || currentWidth,
      viewportWidth - dashboardTabsRect.left
    );
    const rightControlsWidth = getOuterWidth(rightControlsRef.current);

    const availableWidth = Math.max(
      0,
      visibleContainerWidth - rightControlsWidth - NAV_GAP - TAB_LAYOUT_BUFFER
    );

    const cappedTabCount = tabWidths.length;

    for (let count = cappedTabCount; count > 0; count -= 1) {
      const tabsWidth = tabWidths
        .slice(0, count)
        .reduce((total, tabWidth) => total + tabWidth, 0);
      const guttersWidth = Math.max(0, count - 1) * TAB_GUTTER;
      const hasOverflow = count < tabWidths.length;
      const overflowWidth = hasOverflow ? moreButtonWidth + NAV_GAP : 0;

      if (tabsWidth + guttersWidth + overflowWidth <= availableWidth) {
        setNextVisibleTabCount(count);
        return;
      }
    }

    setNextVisibleTabCount(1);
  }, [dashboardItems.items.length]);

  const requestLayoutUpdate = useCallback(() => {
    if (layoutFrameRef.current !== null) {
      return;
    }

    layoutFrameRef.current = window.requestAnimationFrame(() => {
      layoutFrameRef.current = null;
      const currentWidth = getWindowWidth();
      lastWindowWidthRef.current = currentWidth;
      setWidth(currentWidth);
      updateVisibleTabCount();
      setLayoutRevision((revision) => revision + 1);
    });
  }, [updateVisibleTabCount]);

  useEffect(() => {
    window.addEventListener("resize", requestLayoutUpdate);
    window.addEventListener("orientationchange", requestLayoutUpdate);
    window.visualViewport?.addEventListener("resize", requestLayoutUpdate);

    return () => {
      window.removeEventListener("resize", requestLayoutUpdate);
      window.removeEventListener("orientationchange", requestLayoutUpdate);
      window.visualViewport?.removeEventListener("resize", requestLayoutUpdate);
    };
  }, [requestLayoutUpdate]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      const currentWidth = getWindowWidth();
      if (currentWidth !== lastWindowWidthRef.current) {
        requestLayoutUpdate();
      }
    }, 200);

    return () => window.clearInterval(intervalId);
  }, [requestLayoutUpdate]);

  useEffect(() => {
    return () => {
      if (layoutFrameRef.current !== null) {
        window.cancelAnimationFrame(layoutFrameRef.current);
      }

      if (settleTimerRef.current !== null) {
        window.clearTimeout(settleTimerRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    updateVisibleTabCount();

    const frameId = window.requestAnimationFrame(updateVisibleTabCount);

    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
    }

    settleTimerRef.current = window.setTimeout(() => {
      settleTimerRef.current = null;
      updateVisibleTabCount();
    }, 150);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [dashboardItems.items, updateVisibleTabCount, rightTabs, enableLayoutToggle, width, layoutRevision]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(requestLayoutUpdate);

    if (dashboardTabsRef.current) {
      resizeObserver.observe(dashboardTabsRef.current);
    }

    if (rightControlsRef.current) {
      resizeObserver.observe(rightControlsRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [requestLayoutUpdate]);

  const stopTabBarScroll = useCallback((event) => {
    event.stopImmediatePropagation?.();
    event.stopPropagation();
  }, []);

  useEffect(() => {
    const navWrap = tabBarWrapperRef.current?.querySelector(".ant-tabs-nav-wrap");

    if (!navWrap || width < 768) {
      return undefined;
    }

    navWrap.addEventListener("wheel", stopTabBarScroll, true);
    navWrap.addEventListener("touchstart", stopTabBarScroll, true);

    return () => {
      navWrap.removeEventListener("wheel", stopTabBarScroll, true);
      navWrap.removeEventListener("touchstart", stopTabBarScroll, true);
    };
  }, [dashboardItems.items.length, stopTabBarScroll, width]);

  const navigateToTab = (url) => {
    if (setActiveTabKey) {
      setActiveTabKey("1");
    }

    const item = dashboardItems.items.find((item) => item.url === url);
    if (item) {
      navigate(url);
    }
  };

  const renderMoreTabs = () => {
    if (!overflowDashboardItems.length) {
      return null;
    }

    return (
      <Dropdown
        trigger={["click"]}
        placement="bottomLeft"
        menu={{
          selectedKeys: activeOverflowTab ? [current] : [],
          onClick: ({ key }) => navigateToTab(key),
          items: overflowDashboardItems.map((item) => ({
            key: item.url,
            label: (
              <span className="menu-item display-center" style={{ gap: "6px" }}>
                <i className={item.icon} />
                <span>{item.title}</span>
              </span>
            ),
          })),
        }}
      >
        <button
          type="button"
          className={`dashboard-tabs-more-button ${activeOverflowTab ? "active" : ""}`}
        >
          <span>Tabs</span>
          <i className="ri-arrow-down-s-line" style={{ fontSize: "16px" }} />
        </button>
      </Dropdown>
    );
  };

  const filterSwitch = () => {
    return (
      <Flex gap="middle" className="dashboard-tabs-extra" style={{ alignItems: "center" }}>
        {renderMoreTabs()}
        <div className="dashboard-tabs-right-controls" ref={rightControlsRef}>
          {rightTabs && (
            <div className="dashboard-tabs-right-tabs" style={{ marginRight: compactRightTabs ? "8px" : width > 1440 ? "6em" : "1em" }}>
              {rightTabs}
            </div>
          )}
          {width > 1440 ? (
            <div style={{ height: "45px" }}>
              {enableLayoutToggle && (
                <LayoutToggleComponent
                  block={false}
                />
              )}
              <div
                className="display-center mb-3 ms-3"
                style={{ height: "2.5em", gap: "0.5em", marginRight: "1em" }}
              >
                <div style={{ color: 'var(--dms-color-text-muted)' }}>Hide</div>
                <Switch
                  defaultChecked
                  size="small"
                  style={{ width: "2em" }}
                  onChange={setShowFilterPanel}
                  aria-label="Toggle filter panel visibility"
                />
                <div style={{ color: 'var(--dms-color-text)' }}>Show filters</div>
                <Hint
                  icon="ri-question-line"
                  popoverText="You can hide the filter to increase the working area of the table."
                  placement="bottomRight"
                />
              </div>
            </div>
          ) : null}
        </div>
      </Flex>
    );
  } 

  const onChange = (key) => {
    navigateToTab(key);
  };

  const handleTabClick = (url) => {
    navigateToTab(url);
  };

  const renderTabBar = (props, DefaultTabBar) => (
    <div ref={tabBarWrapperRef} style={{ width: "100%" }}>
      <DefaultTabBar
        {...props}
        style={{
          backgroundColor: "#F2F2F2",
          borderBottom: "none"
        }}
        moreIcon={
          <div>
            <i className="ri-more-line" style={{ fontSize: "16px" }} />
          </div>
        }
      />
    </div>
  );

  return (
    <div
      ref={dashboardTabsRef}
      className={`dashboard-tabs ${subTabActive ? "subtabs-active" : ""}`}
      style={{ width: "100%" }}
    >
      <div className="dashboard-tabs-measure" ref={tabMeasureRef} aria-hidden="true">
        {dashboardItems.items.map((item) => (
          <div className="ant-tabs-tab" data-dashboard-tab-measure key={item.url}>
            <div className="ant-tabs-tab-btn">
              <div className="display-center" style={{ gap: "6px" }}>
                <i className={item.icon} />
                <div>{item.title}</div>
              </div>
            </div>
          </div>
        ))}
        <button type="button" className="dashboard-tabs-more-button">
          <span>Tabs</span>
          <i className="ri-arrow-down-s-line" style={{ fontSize: "16px" }} />
        </button>
      </div>
      <ConfigProvider
        theme={{
          components: {
            Tabs: {
              border: "none",
            },
          },
        }}
      >
        {width < 768 ? (
          <>
            <div
              style={{
                margin: "8px 0",
                paddingRight: "10px",
                paddingLeft: "10px",
                height: "34px",
              }}
            >
              <Select
                aria-label="Dashboard"
                size="medium"
                value={current}
                onChange={onChange}
                options={dashboardItems.items.map((item) => ({
                  label: (
                  <div onClick={() => handleTabClick(item.url)}>
                    <span className="display-center" style={{ gap: "6px" }}>
                      <i className={item.icon} />
                      <span>{item.title}</span>
                    </span>
                  </div>
                  ),
                  value: item.url,
                }))}
                style={{
                  width: "100%",
                  borderRadius: "4px",
                  backgroundColor: "var(--dms-color-bg-container)",
                }}
              />
            </div>
            <Flex className="tabs-container">{children}</Flex>
          </>
        ) : (
          <>
          <Tabs
            key={tabsLayoutKey}
            onChange={onChange}
            type="card"
            size="small"
            activeKey={current}
            tabBarGutter={8}
            tabBarExtraContent={filterSwitch()}
            renderTabBar={renderTabBar}
            items={visibleDashboardItems.map((item) => ({
              label: (
                <div 
                  className="display-center" 
                  style={{ gap: "6px", cursor: "pointer" }}
                  onClick={() => handleTabClick(item.url)}
                >
                  <i className={item.icon} />
                  <div>{item.title}</div>
                </div>
              ),
              key: item.url,
              children: null,
            }))}
          />
          <Flex className="tabs-container">{children}</Flex>
          </>
        )}
      </ConfigProvider>
    </div>
  );
};
